import { useMemo, useState, type ReactNode } from 'react';
import { IconArrowLeft, IconChevronDown, IconClockHour4, IconDots, IconExternalLink, IconInfoCircle, IconLink, IconListDetails, IconMail, IconNotes, IconPlus, IconReceipt2, IconSend, IconShip, IconTable, IconTag, IconUser, IconUserCircle, IconCalendarEvent, IconChecklist, IconFileText, IconHistory, IconArrowRight } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { useT } from '@hana/i18n';
import { Button, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { CONT_TYPES, NF, dateOff, feeLine, parseNum, qtyLabel, refOf, suggestedSell, totalCont, validDays, type ContCount } from './editLogic';
import { CALC_LABEL, NATURE_LABEL, ROUTE_MARGIN, MARGIN_FLOOR, resolveLine, marginOf } from './logic';
import { DEFAULT_FEES } from './fixtures';
import type { LineCalc, LineNature } from './types';
import './quoteEdit.i18n';
import s from './quoteEdit.module.css';

// Mint-QuoteEdit (1536): khách & tuyến · phương án cước · local charges · plus services · phụ thu · phát sinh · điều kiện · hoạt động | cột phải 380.
// Chỉ đọc: nút ghi báo toast. Giá mua / margin / GP không có ở màn này (chỉ sau quyền `money`).

const CUSTOMERS = [
  { k: 'THACO', contact: 'Mr Kim · Logistics', line: 'SaleLine DAVID', mg: { LAX: 9, LGB: 8.5 } as Record<string, number> },
  { k: 'GREENBUD', contact: 'Mr Tanaka · Purchase', line: 'SaleLine DAVID', mg: { RTM: 7.5 } as Record<string, number> },
  { k: 'SAVITA', contact: 'Ms Hà · XK', line: 'SaleLine PHILIP', mg: {} as Record<string, number> },
  { k: 'COCOGROUP', contact: 'Ms Linh · XNK', line: 'SaleLine DAVID', mg: { BUS: 7 } as Record<string, number> },
  { k: 'KHANGTRI', contact: 'Ms Trinh · XK', line: 'SaleLine PHILIP', mg: {} as Record<string, number> },
  { k: 'EMDEES', contact: 'Mr Rossi · Import', line: 'SaleLine ANDG', mg: { SYD: 10 } as Record<string, number> },
];
const POLS = ['CLI', 'SGN'];
const PODS = ['LAX', 'LGB', 'RTM', 'HAM', 'BUS', 'SIN', 'JEA', 'SYD', 'MEL'];
const SERVICES = ['Sea FCL', 'Sea LCL', 'Reefer', 'Air', 'Trucking', 'Hải quan'] as const;
const CC_SUG = ['Phòng XNK · chung', 'Kế toán khách', 'pricing@hanalogisticsvn.com'];
const FLOW: readonly [string, string][] = [['Nháp', 'TUANNT · lưu tự động 09:41'], ['Duyệt giá', 'không cần · giá trên sàn'], ['Gửi khách', 'email + PDF + link myHANA'], ['Khách chấp nhận', 'trên myHANA → tạo Job']];
const PAYMENT = ['Theo hợp đồng · 30 ngày', 'Thanh toán trước', '15 ngày từ ngày hoá đơn'];
const NOTE = 'Giá chưa gồm thuế GTGT trừ khi ghi rõ · phát sinh ngoài báo giá tính theo thực tế · giá hãng có thể đổi theo thông báo.';
const SECTION_OPEN = { cus: true, rates: true, local: true, plus: true, sur: true, ext: true, cond: true, act: true, chk: true, info: true, tot: true, mail: true, appr: true, pic: true, lnk: false } as const;
type SecKey = keyof typeof SECTION_OPEN;
const ICO = 18;

interface LineOv { calc?: LineCalc; nature?: LineNature; scope?: string; hf?: number }

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

function Acc({ id, open, toggle, label, aria, icon, tip, tag, tagTone, end, sum, children }: { id: string; open: boolean; toggle: (k: SecKey) => void; label: string; aria?: string; icon: ReactNode; tip?: string; tag?: string; tagTone?: 'ok' | 'warn'; end?: ReactNode; sum: string; children: ReactNode }) {
  return (
    <section className={s.sec} aria-label={aria ?? label}>
      <div className={s.head}>
        <span className={s.hIco}>{icon}</span>
        <h2>{label}</h2>
        {tip && (
          <span className={s.tip} role="img" aria-label={`Giải thích: ${tip}`} title={tip}>
            <IconInfoCircle size={14} stroke={1.9} />
          </span>
        )}
        {tag && <span className={`${s.hTag} ${tagTone === 'ok' ? s.tagOk : tagTone === 'warn' ? s.tagWarn : ''}`}>{tag}</span>}
        <div className={s.hEnd}>
          {end}
          <button type="button" className={`${s.chev} ${open ? s.chevOn : ''}`} aria-expanded={open} aria-label={`Mở hoặc gập ${(aria ?? label).toLowerCase()}`} title="Mở / gập" onClick={() => toggle(id as SecKey)}>
            <IconChevronDown size={ICO} stroke={1.9} />
          </button>
        </div>
      </div>
      {open ? <div className={s.body}>{children}</div> : <div className={s.sum}>{sum}</div>}
    </section>
  );
}

export function QuoteEdit() {
  const tx = useT();
  const toast = useToast();
  const rates = useList('quoteRate', { limit: 100 });
  const feePool = useList('quoteFee', { limit: 100 }).data?.items ?? [];
  const plusAll = useList('quotePlus', { limit: 100 }).data?.items ?? [];
  const surAll = useList('quoteSurcharge', { limit: 100 }).data?.items ?? [];
  const extAll = useList('quoteContingency', { limit: 100 }).data?.items ?? [];
  const forced = readState();

  const [open, setOpen] = useState<Record<SecKey, boolean>>({ ...SECTION_OPEN });
  const [cusI, setCusI] = useState(2);
  const [cusQ, setCusQ] = useState<string | null>(null);
  const [pol, setPol] = useState('CLI');
  const [pod, setPod] = useState('LAX');
  const [cc, setCc] = useState<string[]>(['TU•••@hanalogisticsvn.com']);
  const [svc, setSvc] = useState<Record<string, boolean>>({ 'Sea FCL': true, Trucking: true, 'Hải quan': true });
  const [cn, setCn] = useState<ContCount>({ '20DC': 0, '40DC': 0, '40HC': 2, '40RF': 0 });
  const [optOn, setOptOn] = useState<Record<number, boolean>>({ 0: true, 1: true });
  const [sellOv, setSellOv] = useState<Record<number, number>>({});
  const [reason, setReason] = useState('');
  const [fx, setFx] = useState(25420);
  const [fees, setFees] = useState<string[]>([...DEFAULT_FEES]);
  const [feeP, setFeeP] = useState<Record<string, number>>({});
  const [feeCur, setFeeCur] = useState<Record<string, 'USD' | 'VND'>>({});
  const [feeOpen, setFeeOpen] = useState(false);
  const [plusOn, setPlusOn] = useState<Record<string, boolean>>({ CCF: true, TRFA: true, LNF: true, XLH: true });
  const [plusP, setPlusP] = useState<Record<string, number>>({});
  const [ov, setOv] = useState<Record<string, LineOv>>({});
  const [surOff, setSurOff] = useState<string[]>([]);
  const [surP, setSurP] = useState<Record<string, number>>({});
  const [extOff, setExtOff] = useState<string[]>([]);
  const [extNew, setExtNew] = useState<string[]>([]);
  const [extIn, setExtIn] = useState('');
  const [valid, setValid] = useState<'7' | '14' | '31'>('14');
  const [verOpen, setVerOpen] = useState(false);

  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const toggle = (k: SecKey) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const patch = (code: string, p: LineOv) => setOv((o) => ({ ...o, [code]: { ...o[code], ...p } }));

  const cus = CUSTOMERS[cusI] ?? CUSTOMERS[2]!;
  const routeK = `${pol} → ${pod}`;
  const totCont = totalCont(cn);
  const qtyK = qtyLabel(cn);
  const reefer = cn['40RF'] > 0 || !!svc.Reefer;
  const refK = refOf(reefer, pol, pod, 77);

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (rates.isLoading) state = 'loading';
  else if (rates.isError) state = rates.error instanceof DataError && rates.error.code === 'noperm' ? 'noperm' : 'error';
  const ready = state === 'ready' || state === 'empty';

  const sugMg = cus.mg[pod] ?? ROUTE_MARGIN[pod] ?? 8;
  const raw = useMemo(() => (state === 'empty' || pod === 'MEL' ? [] : (rates.data?.items ?? []).filter((r) => r.pod === (['LAX', 'RTM', 'BUS'].includes(pod) ? pod : 'LAX'))), [rates.data, pod, state]);
  const opts = raw.map((r, i) => {
    const sug = suggestedSell(r, sugMg);
    const sell = sellOv[i] ?? sug;
    return { i, r, sug, sell, under: marginOf(sell, r.buy) < MARGIN_FLOOR, over: sell > sug, on: !!optOn[i] };
  });
  const minBuy = raw.length ? Math.min(...raw.map((r) => r.buy)) : 0;
  const picked = opts.filter((o) => o.on);
  const main = picked[0] ?? opts[0] ?? null;
  const needApprove = picked.some((o) => o.under);
  const overList = picked.filter((o) => o.over && !o.under);
  const needReason = overList.length > 0 && !needApprove;
  const rateVd = main ? main.r.validDays : 14;
  const vDays = validDays(valid, rateVd);

  const feeRows = fees.flatMap((code) => {
    const f = feePool.find((p) => p.code === code);
    if (!f) return [];
    const cur = feeCur[code] ?? f.cur;
    return [{ f, cur, ...feeLine(f, cur, feeP[code], fx, totCont) }];
  });
  const localVnd = feeRows.reduce((a, x) => a + x.vnd, 0);
  const localVat = feeRows.reduce((a, x) => a + x.vat, 0);
  const totSellVnd = (main ? main.sell * totCont * fx : 0) + localVnd + localVat;

  const stK = 'Nháp';
  const sentTodo: { k: string; sub: string; tone: 'ok' | 'warn' | 'err' | 'info'; act?: string }[] = [];
  if (needApprove) sentTodo.push({ k: 'Giá dưới sàn · cần [SAL-MGR] DAVID duyệt', sub: 'việc DUYỆT hạn 4 giờ LV', tone: 'warn', act: 'Xem giá' });
  if (needReason) sentTodo.push({ k: 'Ghi lý do giá cao hơn gợi ý', sub: `${overList.map((o) => o.r.carrier).join(', ')} · bắt buộc trước khi gửi`, tone: reason ? 'ok' : 'warn', act: 'Ghi lý do' });
  if (ready && raw.length === 0) sentTodo.push({ k: `Tuyến ${routeK} chưa có giá hãng`, sub: 'tạo phiếu hỏi giá · task PRC hạn 24 giờ LV', tone: 'warn', act: 'Hỏi Pricing' });
  if (raw.length > 0 && picked.length === 0) sentTodo.push({ k: 'Chưa chọn phương án nào', sub: 'tích ít nhất 1 phương án để gửi', tone: 'err', act: 'Chọn' });
  if (picked.length > 0) sentTodo.push({ k: 'Chọn phương án gửi khách', sub: `${picked.map((o) => o.r.carrier).join(', ')} · chính ${main?.r.carrier}`, tone: 'ok' });
  if (picked.length > 0 && !needApprove && !needReason) sentTodo.push({ k: 'Giá bán trong khung', sub: 'không cần duyệt · không cần lý do', tone: 'ok' });
  if (main && vDays < (valid === '7' ? 7 : valid === '14' ? 14 : 31)) sentTodo.push({ k: 'Hiệu lực cắt theo giá hãng', sub: `đến ${dateOff(vDays)} · giá ${main.r.carrier} còn ${main.r.validDays} ngày`, tone: 'info', act: 'Xem' });
  sentTodo.push({ k: 'Người nhận và CC', sub: `${cus.contact} · ${cc.length} CC`, tone: 'ok' });
  const todoOpen = sentTodo.filter((k) => k.tone !== 'ok' && k.tone !== 'info').length;

  const svcOn = SERVICES.filter((k) => svc[k]);
  const info: [string, string][] = [['Ref', `${refK} · bản 1`], ['Khách', cus.k], ['Người nhận', cus.contact], ['Tuyến', routeK], ['Dịch vụ', svcOn.join(' + ') || '—'], ['Số lượng', qtyK], ['Hiệu lực đến', dateOff(vDays)], ['Trạng thái', stK]];
  const pic = [{ u: 'TUANNT', role: 'Sale · người lập · nhận xử lý' }, { u: cus.line.replace('SaleLine ', ''), role: 'Sale · theo khách' }, { u: 'MAITTN', role: 'CS · theo khách' }, { u: '', role: 'Sale Asst · theo khách' }, { u: 'LIENNTK', role: 'Kế toán · theo khách' }];
  const links: [string, string][] = [[`Khách hàng · ${cus.k}`, 'Mở'], [`Bảng giá hãng · ${routeK}`, 'Mở'], ['Giá và Lịch', 'Tra'], ['Hỏi giá đối tác / đại lý', 'Mở'], ['Biểu giá dịch vụ', 'Mở'], ['Danh sách báo giá', 'Mở']];
  const acts: [string, string, string][] = [['Lưu tự động bản nháp', '22/Oct 09:41', 'var(--d-gray)'], ['Bản 1 · 2×40HC · YML · 2 phương án · DAVID', '15/Oct 08:40', 'var(--d-violet)'], [`Tạo báo giá ${refK} · TUANNT`, '22/Oct 09:05', 'var(--d-gray)']];

  const surRows = surAll.filter((x) => !surOff.includes(x.key));
  const extRows = [...extAll.filter((x) => !extOff.includes(x.id)).map((x) => ({ id: x.id, code: x.code, label: x.label })), ...extNew.map((l) => ({ id: `n:${l}`, code: '', label: l }))];

  const hfOf = (code: string) => { const l = resolveLine(code, ov[code]); return l; };
  const lineCtl2 = (code: string) => {
    const l = hfOf(code);
    const setHf = (v: number) => setOv((o) => ({ ...o, [code]: { ...o[code], hf: v } }));
    return (
      <div className={s.qlIn}>
        <select className={s.qlSel} aria-label={`Cách tính ${code}`} title="Cách tính" value={l.calc} onChange={(e) => patch(code, { calc: e.target.value as LineCalc })}>
          {(Object.keys(CALC_LABEL) as LineCalc[]).map((k) => <option key={k} value={k}>{CALC_LABEL[k]}</option>)}
        </select>
        <div className={s.nat} role="radiogroup" aria-label={`Bản chất ${code}`} title="Bản chất">
          {(Object.keys(NATURE_LABEL) as LineNature[]).map((k) => (
            <button key={k} type="button" role="radio" aria-checked={l.nature === k} className={`${s.natB} ${l.nature === k ? s.natOn : ''}`} onClick={() => patch(code, { nature: k })}>{NATURE_LABEL[k]}</button>
          ))}
        </div>
        {l.calc === 'ahf' && (
          <label className={s.hf} title="Phí xử lý chi hộ">{tx('qe.1')}
            <input inputMode="numeric" aria-label={`Phí xử lý chi hộ ${code}`} value={NF(l.handlingFee)} onChange={(e) => setHf(parseNum(e.target.value))} />₫
          </label>
        )}
        {(l.scope !== '' || l.nature === 'inc') && (
          <input className={s.scope} aria-label={`Phạm vi ${code}`} title="Phạm vi" placeholder="Phạm vi" value={l.scope} onChange={(e) => patch(code, { scope: e.target.value })} />
        )}
      </div>
    );
  };

  const cusOpts = CUSTOMERS.map((c, i) => ({ c, i })).filter(({ c }) => !cusQ || c.k.toLowerCase().includes(cusQ.toLowerCase()));
  const statusChip = (
    <span className={s.chipSt}><span className={s.dot} style={{ background: 'var(--d-gray)' }} />{stK}</span>
  );

  const left = (
    <>
      <Acc id="cus" open={open.cus} toggle={toggle} label="Khách & tuyến" aria="Khách và tuyến" icon={<IconUser size={ICO} stroke={1.9} />} tip="Khách theo CUSDATA (Taxcode, SaleLine) · tuyến POL → POD · dịch vụ và số cont dùng để lấy giá hãng." sum={`${cus.k} · ${routeK} · ${qtyK}`}>
        <div className={s.grid4}>
          <div className={s.rel}>
            <label><div className={s.lbl}>{tx('qe.2')}</div>
              <input className={s.inp} role="combobox" aria-expanded={cusQ !== null} aria-label="Khách hàng" value={cusQ ?? cus.k} onChange={(e) => setCusQ(e.target.value)} onFocus={() => setCusQ('')} />
            </label>
            {cusQ !== null && (
              <div className={s.pop} role="listbox" aria-label="Chọn khách hàng">
                {cusOpts.map(({ c, i }) => (
                  <button key={c.k} type="button" role="option" aria-selected={i === cusI} className={`${s.opt} ${i === cusI ? s.optOn : ''}`} onClick={() => { setCusI(i); setCusQ(null); }}>
                    <b>{c.k}</b><span className={s.sub}>{c.contact} · {c.line}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <label><div className={s.lbl}>{tx('qe.3')}</div><select className={s.sel} aria-label="Người nhận"><option>{cus.contact}</option></select></label>
          <label><div className={s.lbl}>POL</div><select className={s.sel} aria-label="Cảng đi" value={pol} onChange={(e) => setPol(e.target.value)}>{POLS.map((p) => <option key={p}>{p}</option>)}</select></label>
          <label><div className={s.lbl}>POD</div><select className={s.sel} aria-label="Cảng đến" value={pod} onChange={(e) => setPod(e.target.value)}>{PODS.map((p) => <option key={p}>{p}</option>)}</select></label>
        </div>
        <div className={s.ccRow}>
          <span className={s.ccK}>CC</span>
          {cc.map((k) => (
            <span key={k} className={s.ccChip}>{k}<button type="button" className={s.del} aria-label={`Bỏ CC ${k}`} onClick={() => setCc(cc.filter((x) => x !== k))}>×</button></span>
          ))}
          {CC_SUG.filter((k) => !cc.includes(k)).map((k) => <button key={k} type="button" className={s.ccAdd} onClick={() => setCc([...cc, k])}><IconPlus size={14} stroke={1.8} style={{ verticalAlign: -3, marginRight: 6 }} />{k}</button>)}
        </div>
        <div className={s.row2}>
          <div>
            <div className={s.lbl}>{tx('qe.4')}</div>
            <div className={s.tgs} role="group" aria-label="Dịch vụ">
              {SERVICES.map((k) => <button key={k} type="button" aria-pressed={!!svc[k]} className={`${s.tog} ${svc[k] ? s.togOn : ''}`} onClick={() => setSvc({ ...svc, [k]: !svc[k] })}>{k}</button>)}
            </div>
          </div>
          <div>
            <div className={s.lbl}>{tx('qe.5')}</div>
            <div className={s.conts}>
              {CONT_TYPES.map((k) => (
                <div key={k} className={`${s.cont} ${cn[k] > 0 ? s.contOn : ''}`}>
                  <span>{k}</span>
                  <button type="button" className={s.stp} aria-label={`Bớt ${k}`} onClick={() => setCn({ ...cn, [k]: Math.max(0, cn[k] - 1) })}>−</button>
                  <b>{cn[k]}</b>
                  <button type="button" className={s.stp} aria-label={`Thêm ${k}`} onClick={() => setCn({ ...cn, [k]: cn[k] + 1 })}>+</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Acc>

      <Acc id="rates" open={open.rates} toggle={toggle} label={`Phương án cước · ${routeK}`} aria="Phương án cước" icon={<IconShip size={ICO} stroke={1.9} />} tip={`Từ bảng giá hãng hiệu lực 22/Oct/2026 · ${cus.mg[pod] != null ? `margin cài sẵn cho ${cus.k}` : `khách chưa cài margin tuyến này · dùng margin chuẩn tuyến ${sugMg}%`}`} sum={`${picked.length} phương án · chính ${main?.r.carrier ?? '—'}`}
        end={<a className={s.hLink} href="/ezy/rates"><IconExternalLink size={16} stroke={1.8} />{tx('qe.6')}</a>}>
        {raw.length === 0 ? (
          <div className={s.empty}>
            <div><b>Chưa có giá hãng cho {routeK}</b><div className={s.sub}>{tx('qe.7')}</div></div>
            <Button variant="primary" onClick={noWrite}>{tx('qe.8')}</Button>
          </div>
        ) : (
          <>
            <table className={s.tbl}>
              <thead><tr>
                <th style={{ width: 36 }} /><th style={{ width: 92 }}>{tx('qe.9')}</th><th>{tx('qe.10')}</th><th style={{ width: 104 }}>Transit</th><th style={{ width: 98 }}>{tx('qe.11')}</th><th className={s.r} style={{ width: 124 }}>{tx('qe.12')}</th>
              </tr></thead>
              <tbody>
                {opts.map((o) => (
                  <tr key={o.i} className={s.rate}>
                    <td style={{ paddingLeft: 4 }}><input type="checkbox" className={s.cb} aria-label={`Gửi phương án ${o.r.carrier}`} checked={o.on} onChange={() => setOptOn({ ...optOn, [o.i]: !o.on })} /></td>
                    <td><b style={{ fontWeight: 550 }}>{o.r.carrier}</b>{o.r.buy === minBuy && raw.length > 1 && <div className={s.best}>{tx('qe.13')}</div>}</td>
                    <td className={s.ell}>{o.r.vessel}<div className={s.rsub} title={`ETD ${o.r.etd}`}><IconCalendarEvent size={14} stroke={1.8} />{o.r.etd}/2026</div></td>
                    <td className={s.ell} title={`Transit ${o.r.transit}`}>{o.r.transit.split(' · ')[0]}<div className={s.sub}>{o.r.transit.split(' · ').slice(1).join(' · ')}</div></td>
                    <td title={`Hiệu lực còn ${o.r.validDays} ngày`} style={{ color: o.r.validDays <= 10 ? 'var(--warn)' : 'var(--tx2)' }}><span className={s.rsub} style={{ color: 'inherit' }}><IconClockHour4 size={14} stroke={1.8} />{o.r.validDays} ngày</span></td>
                    <td className={s.r}><input className={s.sell} aria-label={`Giá bán ${o.r.carrier}`} value={o.sell.toLocaleString('en-US')} onChange={(e) => setSellOv({ ...sellOv, [o.i]: parseNum(e.target.value) })} /><div className={s.sub}>gợi ý {o.sug.toLocaleString('en-US')}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {needApprove && (
              <div className={s.warn} role="alert"><b style={{ fontSize: 18 }}>!</b><div style={{ flexGrow: 1 }}><b style={{ fontWeight: 550 }}>{tx('qe.14')}</b><div style={{ color: 'var(--tx2)' }}>{tx('qe.15')}</div></div>
                <button type="button" className={s.btn} onClick={() => setSellOv({})}>{tx('qe.16')}</button></div>
            )}
            {needReason && (
              <label style={{ display: 'block', marginTop: 12, padding: '12px 14px', borderRadius: 12, background: 'var(--sub)' }}>
                <div style={{ fontWeight: 550, marginBottom: 6 }}>{overList.map((o) => o.r.carrier).join(', ')} cao hơn giá gợi ý · ghi lý do</div>
                <input className={s.inp} aria-label="Lý do giá cao hơn gợi ý" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="VD: khách cần free time 14 ngày, hàng cồng kềnh" />
              </label>
            )}
          </>
        )}
      </Acc>

      <Acc id="local" open={open.local} toggle={toggle} label="Local charges" aria="Phụ phí địa phương" icon={<IconListDetails size={ICO} stroke={1.9} />} tip="Từ kho phụ phí · VAT theo mã phí" sum={`${feeRows.length} phí · ${NF(localVnd + localVat)} ₫`}
        end={(
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label className={s.fx}>{tx('qe.17')}<input aria-label="Tỷ giá USD sang VND" value={NF(fx)} onChange={(e) => setFx(parseNum(e.target.value) || 1)} /></label>
            <div className={s.rel}>
              <button type="button" className={s.btn} aria-haspopup="true" aria-expanded={feeOpen} onClick={() => setFeeOpen(!feeOpen)}><IconPlus size={14} stroke={1.8} />{tx('qe.18')}</button>
              {feeOpen && (
                <div className={s.poolPop} role="dialog" aria-label="Kho phụ phí">
                  {feePool.filter((p) => !fees.includes(p.code)).map((p) => (
                    <button key={p.code} type="button" className={s.opt} onClick={() => { setFees([...fees, p.code]); setFeeOpen(false); }}><span><b className={s.code}>{p.code}</b> {p.label}</span><span className={s.sub}>{p.cur === 'USD' ? `${p.price} USD` : `${NF(p.price)} ₫`} / {p.unit}</span></button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}>
        <table className={s.tbl}>
          <thead><tr>
            <th style={{ width: 72 }}>{tx('qe.19')}</th><th>{tx('qe.20')}</th><th style={{ width: 90 }}>{tx('qe.21')}</th><th className={s.r} style={{ width: 44 }}>SL</th><th className={s.r} style={{ width: 120 }}>{tx('qe.22')}</th><th style={{ width: 104, paddingLeft: 12 }}>{tx('qe.23')}</th><th className={s.r} style={{ width: 56 }}>VAT</th><th className={s.r} style={{ width: 124 }}>{tx('qe.24')}</th><th style={{ width: 36 }} />
          </tr></thead>
          <tbody>
            {feeRows.map(({ f, cur, base, qty, vnd, vat }) => [
              <tr key={f.code} className={s.fee}>
                <td className={s.code}>{f.code}</td>
                <td className={s.ell}>{f.label}</td>
                <td style={{ color: 'var(--tx2)' }}>{f.unit}</td>
                <td className={s.r}>{qty}</td>
                <td className={s.r}><input className={s.num} style={{ width: 104 }} aria-label={`Đơn giá ${f.code}`} value={NF(base)} onChange={(e) => setFeeP({ ...feeP, [f.code]: parseNum(e.target.value) })} /></td>
                <td style={{ paddingLeft: 12 }}>
                  <div className={s.cur} role="group" aria-label={`Tiền tệ ${f.code}`}>
                    {(['USD', 'VND'] as const).map((c) => <button key={c} type="button" aria-pressed={cur === c} className={`${s.curB} ${cur === c ? s.curOn : ''}`} onClick={() => { const p = { ...feeP }; delete p[f.code]; setFeeP(p); setFeeCur({ ...feeCur, [f.code]: c }); }}>{c}</button>)}
                  </div>
                </td>
                <td className={s.r} style={{ color: 'var(--tx2)' }}>{f.vat}%</td>
                <td className={s.r} style={{ fontWeight: 550 }}>{NF(vnd + vat)}</td>
                <td className={s.r}><button type="button" className={s.del} aria-label={`Bỏ phí ${f.code}`} onClick={() => setFees(fees.filter((x) => x !== f.code))}>×</button></td>
              </tr>,
              <tr key={`${f.code}-q`} className={s.ql}><td /><td colSpan={8}>{lineCtl2(f.code)}</td></tr>,
            ])}
          </tbody>
        </table>
      </Acc>

      <Acc id="plus" open={open.plus} toggle={toggle} label="Plus Services" aria="Dịch vụ cộng thêm" icon={<IconPlus size={ICO} stroke={1.9} />} tip="Giá riêng của khách nếu có, không thì theo biểu giá dịch vụ chuẩn · tích để in vào báo giá" sum={`${plusAll.filter((x) => plusOn[x.code]).length} dịch vụ kèm`}
        end={<a className={s.hLink} href="/ezy/service-price"><IconTable size={16} stroke={1.8} />{tx('qe.25')}</a>}>
        <table className={s.tbl}>
          <thead><tr>
            <th style={{ width: 34 }} /><th style={{ width: 64 }}>{tx('qe.26')}</th><th>{tx('qe.27')}</th><th style={{ width: 70 }}>{tx('qe.21')}</th><th className={s.r} style={{ width: 130 }}>20′ (₫)</th><th className={s.r} style={{ width: 130 }}>40′ (₫)</th><th style={{ width: 122, paddingLeft: 12 }}>{tx('qe.28')}</th>
          </tr></thead>
          <tbody>
            {plusAll.map((x) => [
              <tr key={x.code} className={s.fee}>
                <td><input type="checkbox" className={s.cb} aria-label={`Kèm ${x.code}`} checked={!!plusOn[x.code]} onChange={() => setPlusOn({ ...plusOn, [x.code]: !plusOn[x.code] })} /></td>
                <td className={s.code}>{x.code}</td>
                <td className={s.ell}>{x.label}</td>
                <td style={{ color: 'var(--tx2)' }}>{x.unit}</td>
                <td className={s.r}><input className={s.num} style={{ width: 110 }} aria-label={`Giá 20 ${x.code}`} value={NF(plusP[`${x.code}a`] ?? x.p20)} onChange={(e) => setPlusP({ ...plusP, [`${x.code}a`]: parseNum(e.target.value) })} /></td>
                <td className={s.r}><input className={s.num} style={{ width: 110 }} aria-label={`Giá 40 ${x.code}`} value={NF(plusP[`${x.code}b`] ?? x.p40)} onChange={(e) => setPlusP({ ...plusP, [`${x.code}b`]: parseNum(e.target.value) })} /></td>
                <td style={{ paddingLeft: 12, fontSize: 12, color: x.own ? 'var(--link)' : 'var(--lb)' }}>{x.own ? 'Giá riêng khách' : 'Biểu giá chuẩn'}</td>
              </tr>,
              <tr key={`${x.code}-q`} className={s.ql}><td /><td /><td colSpan={5}>{lineCtl2(x.code)}</td></tr>,
            ])}
          </tbody>
        </table>
      </Acc>

      <Acc id="sur" open={open.sur} toggle={toggle} label="Phụ thu theo biểu" icon={<IconReceipt2 size={ICO} stroke={1.8} />} tag="TRFA" sum={`${surRows.length} khoản · TRFA`}>
        {surRows.map((x) => (
          <div key={x.key} className={s.sur}>
            <span>{x.code && <b className={s.code}>{x.code}</b>}</span>
            <span>{x.label}</span>
            <span className={s.f} title="Công thức">{x.formula.replace('{p}', NF(surP[x.key] ?? x.price))}</span>
            <span>{x.price > 0 && <input className={s.num} style={{ width: 116 }} inputMode="numeric" aria-label={`Giá ${x.label}`} value={NF(surP[x.key] ?? x.price)} onChange={(e) => setSurP({ ...surP, [x.key]: parseNum(e.target.value) })} />}</span>
            <button type="button" className={s.del} aria-label={`Bỏ ${x.label}`} title="Bỏ" onClick={() => setSurOff([...surOff, x.key])}>×</button>
          </div>
        ))}
        {surOff.length > 0 && (
          <div className={s.chips} style={{ marginTop: 12 }}>
            {surOff.map((k) => <button key={k} type="button" className={s.btn} onClick={() => setSurOff(surOff.filter((x) => x !== k))}><IconPlus size={14} stroke={1.8} />{surAll.find((x) => x.key === k)?.label}</button>)}
          </div>
        )}
      </Acc>

      <Acc id="ext" open={open.ext} toggle={toggle} label="Phát sinh có thể có" icon={<IconNotes size={ICO} stroke={1.8} />} tag="Theo thực tế" sum={extRows.map((x) => x.label).join(' · ') || '—'}>
        <div className={s.chips}>
          {extRows.map((x) => (
            <span key={x.id} className={s.chip}>{x.code && <b>{x.code}</b>}{x.label}
              <button type="button" className={s.del} aria-label={`Bỏ ${x.code ? `${x.code} · ` : ''}${x.label}`} title="Bỏ" onClick={() => (x.id.startsWith('n:') ? setExtNew(extNew.filter((l) => `n:${l}` !== x.id)) : setExtOff([...extOff, x.id]))}>×</button>
            </span>
          ))}
        </div>
        <div className={s.addRow}>
          <input aria-label="Thêm phát sinh có thể có" placeholder="Ví dụ: phí lưu kho CFS" value={extIn} onChange={(e) => setExtIn(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && extIn.trim()) { setExtNew([...extNew, extIn.trim()]); setExtIn(''); } }} />
          <button type="button" className={s.btn} onClick={() => { if (extIn.trim()) { setExtNew([...extNew, extIn.trim()]); setExtIn(''); } }}><IconPlus size={15} stroke={1.8} />{tx('qe.29')}</button>
        </div>
      </Acc>

      <Acc id="cond" open={open.cond} toggle={toggle} label="Điều kiện & hiệu lực" aria="Điều kiện và hiệu lực" icon={<IconCalendarEvent size={ICO} stroke={1.9} />} tip="Hiệu lực không vượt hiệu lực giá hãng · điều khoản thanh toán theo hợp đồng khách · điều kiện in kèm báo giá." sum={`đến ${dateOff(vDays)} · ${PAYMENT[0]}`}>
        <div className={s.grid2}>
          <div>
            <div className={s.lbl}>{tx('qe.30')}</div>
            <div className={s.seg3} role="radiogroup" aria-label="Hiệu lực">
              {([['7', '7 ngày'], ['14', '14 ngày'], ['31', 'Theo giá hãng']] as const).map(([k, l]) => <button key={k} type="button" role="radio" aria-checked={valid === k} className={valid === k ? s.on : ''} onClick={() => setValid(k)}>{l}</button>)}
            </div>
            <div className={s.sub} style={{ marginTop: 6 }}>Đến {dateOff(vDays)} · không quá hiệu lực giá hãng ({dateOff(rateVd)})</div>
          </div>
          <label><div className={s.lbl}>{tx('qe.31')}</div><select className={s.sel} aria-label="Điều khoản thanh toán">{PAYMENT.map((p) => <option key={p}>{p}</option>)}</select></label>
          <label className={s.span2}><div className={s.lbl}>{tx('qe.32')}</div><textarea className={s.ta} rows={3} defaultValue={NOTE} /></label>
        </div>
      </Acc>

      <Acc id="act" open={open.act} toggle={toggle} label="Hoạt động" icon={<IconHistory size={ICO} stroke={1.9} />} tip="Phiên bản cùng Ref, lưu tự động, gửi duyệt / gửi khách. Ghi vào lịch sử báo giá (TblQuotation)." tag={String(acts.length)} sum={`${acts.length} sự kiện · gần nhất ${acts[0]?.[1] ?? ''}`}>
        {acts.map(([k, at, c]) => <div key={k} className={s.act}><span className={s.dot} style={{ background: c }} /><span>{k}</span><span>{at}</span></div>)}
      </Acc>
    </>
  );

  const right = (
    <>
      <Acc id="chk" open={open.chk} toggle={toggle} label="Việc cần làm" icon={<IconChecklist size={ICO} stroke={1.9} />} tip="Kiểm tra trước khi gửi: giá dưới sàn cần duyệt, giá cao hơn gợi ý cần lý do, tuyến chưa có giá hãng, hiệu lực bị cắt theo giá hãng." tag={todoOpen ? `${todoOpen} cần xử lý` : 'đủ'} tagTone={todoOpen ? 'warn' : 'ok'} sum={todoOpen ? `${todoOpen} cần xử lý` : 'đủ'}>
        {sentTodo.map((k) => (
          <div key={k.k} className={s.todo}>
            <span className={`${s.mk} ${k.tone === 'ok' ? s.mkOk : k.tone === 'warn' ? s.mkWarn : k.tone === 'err' ? s.mkErr : ''}`} role="img" aria-label={k.tone === 'ok' ? 'Xong' : k.tone === 'info' ? 'Lưu ý' : 'Cần làm'} title={k.tone === 'ok' ? 'Xong' : 'Cần làm'}>{k.tone === 'ok' ? '✓' : k.tone === 'info' ? 'i' : ''}</span>
            <div className={s.tMain}><div>{k.k}</div><div>{k.sub}</div></div>
            {k.act && <button type="button" className={s.tAct} onClick={() => (k.act === 'Hỏi Pricing' ? noWrite() : setOpen((o) => ({ ...o, rates: true })))}>{k.act}</button>}
          </div>
        ))}
      </Acc>
      <Acc id="info" open={open.info} toggle={toggle} label="Thông tin chính" icon={<IconFileText size={ICO} stroke={1.9} />} tip="Ref theo TblQuotation (Q + năm + FCL + RF/DC + POL + POD + số). Đổi tuyến / loại hàng → Ref mới." sum={`${refK} · ${cus.k} · ${routeK}`}>
        {info.map(([k, v]) => <div key={k} className={s.info}><span>{k}</span><span title={v}>{v}</span></div>)}
      </Acc>
      <Acc id="tot" open={open.tot} toggle={toggle} label="Tổng báo giá" icon={<IconTag size={ICO} stroke={1.9} />} tip="Tổng bán = cước phương án chính × số cont + local charges + VAT. Giá mua và margin chỉ hiện khi có quyền xem." sum={`${NF(totSellVnd / fx)} USD · ${NF(totSellVnd)} ₫`}>
        <div className={s.sub}>Tổng bán · phương án {main?.r.carrier ?? '—'}</div>
        <div className={s.big}>{NF(totSellVnd / fx)} <small>USD</small></div>
        <div style={{ color: 'var(--tx2)' }}>≈ {NF(totSellVnd)} ₫ · gồm VAT {NF(localVat)} ₫</div>
      </Acc>
      <Acc id="mail" open={open.mail} toggle={toggle} label="Xem trước email" icon={<IconMail size={ICO} stroke={1.9} />} tip="Email + PDF + link myHANA gửi khách. Chấp nhận trên myHANA → hệ thống tạo Job." end={<span className={s.hTag}>tới {cus.contact}</span>} sum={`tới ${cus.contact}`}>
        <div style={{ fontSize: 12, color: 'var(--tx2)' }}>{tx('qe.33')} <b style={{ fontWeight: 550, color: 'var(--tx)' }}>[HANA] Báo giá {refK} · {routeK} · {qtyK}</b></div>
        <div style={{ height: 1, margin: '12px -20px', background: 'var(--line)' }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className={s.mailLn}>Kính gửi {cus.contact}, HANA gửi báo giá {svcOn.join(' + ') || '—'} tuyến {routeK} như sau:</div>
          <table className={s.mailT}>
            <thead><tr><th>{tx('qe.9')}</th><th>Transit</th><th className={s.r}>{tx('qe.34')}</th></tr></thead>
            <tbody>
              {picked.map((o) => <tr key={o.i}><td style={{ fontWeight: 550 }}>{o.r.carrier}</td><td>{o.r.transit}</td><td className={s.r}>{o.sell.toLocaleString('en-US')} USD</td></tr>)}
              <tr><td colSpan={2}>Local charges</td><td className={s.r}>{NF(localVnd + localVat)} ₫</td></tr>
              {plusAll.filter((x) => plusOn[x.code]).map((x) => { const a = plusP[`${x.code}a`] ?? x.p20; const b = plusP[`${x.code}b`] ?? x.p40; return <tr key={x.code}><td colSpan={2}>{x.code} · {x.label.split(' · ')[0]}</td><td className={s.r}>{a === b ? `${NF(a)} ₫/${x.unit}` : `${NF(a)} / ${NF(b)} ₫`}</td></tr>; })}
            </tbody>
          </table>
          <div className={s.mailLn}>Hiệu lực đến {dateOff(vDays)}.</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', height: 34, padding: '0 14px', borderRadius: 10, background: 'var(--pri)', color: 'var(--on-color)', fontWeight: 550 }}>{tx('qe.35')}</span>
          </div>
        </div>
      </Acc>
      <Acc id="appr" open={open.appr} toggle={toggle} label="Trạng thái & duyệt" aria="Trạng thái và duyệt" icon={<IconArrowRight size={ICO} stroke={1.9} />} tip="Nháp → (dưới sàn: [SAL-MGR] duyệt, việc DUYỆT hạn 4 giờ làm việc) → Gửi khách → Khách chấp nhận trên myHANA → Job. Người duyệt theo ghế." sum={stK + (needApprove ? ' · cần [SAL-MGR] DAVID duyệt' : '')}>
        {FLOW.map(([k, sub], i) => {
          const skip = i === 1 && !needApprove;
          const cur = i === 0;
          return (
            <div key={k} className={s.flow}>
              <span className={`${s.fMk} ${cur ? s.fCur : ''}`} aria-hidden="true">{skip ? '–' : i + 1}</span>
              <div><div style={{ fontWeight: cur ? 600 : 450 }}>{k}</div><div className={s.sub}>{i === 1 && needApprove ? '[SAL-MGR] DAVID · hạn 4 giờ LV' : sub}</div></div>
            </div>
          );
        })}
      </Acc>
      <Acc id="pic" open={open.pic} toggle={toggle} label="Người phụ trách" icon={<IconUserCircle size={ICO} stroke={1.9} />} tip="Hai nguồn, gộp và bỏ trùng: (1) theo khách: Sale, CS, Sale Asst, Kế toán ở hồ sơ khách (CUSDATA); (2) nhận xử lý / được giao: người thực hiện trên chính bản ghi theo mô tả công việc (TblOrgJD). Chưa có người hiện Chưa giao; nút Giao chỉ hiện khi có quyền. Avatar theo TblStaff.ImageProfile. Báo giá: Sale; duyệt giá dưới sàn: Trưởng phòng KD." sum={pic.map((p) => p.u || 'Chưa giao').join(' · ')}>
        {pic.map((p) => (
          <div key={p.role} className={s.pic} title={p.role}>
            <span className={s.av} aria-hidden="true">{p.u ? p.u.slice(0, 2).toUpperCase() : '?'}</span>
            <div className={s.picT}><div>{p.u || 'Chưa giao'}</div><div className={s.sub}>{p.role}</div></div>
            {!p.u && state !== 'noperm' && <button type="button" className={s.give} aria-label={`Giao ${p.role}`} onClick={noWrite}>Giao</button>}
          </div>
        ))}
      </Acc>
      <Acc id="lnk" open={open.lnk} toggle={toggle} label="Liên kết" icon={<IconLink size={ICO} stroke={1.9} />} tip="Mở nhanh các màn liên quan tới báo giá này." sum={`${links.length} màn liên quan`}>
        {links.map(([k, go]) => <a key={k} className={s.lnk} href="/ezy/quotes"><span><IconExternalLink size={16} stroke={1.8} />{k}</span><span>{go}</span></a>)}
      </Acc>
    </>
  );

  return (
    <Page kind="detail">
      <PageHeader
        title={refK}
        lead={<a className={s.back} href="/ezy/quotes" aria-label="Về danh sách báo giá" title="Về danh sách báo giá"><IconArrowLeft size={18} stroke={1.9} /></a>}
        meta={(
          <>
            <div className={s.rel}>
              <button type="button" className={s.ver} aria-haspopup="dialog" aria-expanded={verOpen} title="Phiên bản của cùng Ref" onClick={() => setVerOpen(!verOpen)}>{tx('qe.36')}</button>
              {verOpen && (
                <div className={s.verPop} role="dialog" aria-label="Phiên bản báo giá">
                  <b style={{ fontWeight: 600 }}>Phiên bản · {refK}</b>
                  <div className={s.verRow}><b style={{ fontWeight: 550, width: 50 }}>{tx('qe.37')}</b><span className={s.sub} style={{ flexGrow: 1 }}>{tx('qe.38')}</span></div>
                  <Button variant="primary" onClick={noWrite}>{tx('qe.39')}</Button>
                </div>
              )}
            </div>
            {statusChip}
            {needApprove && <span className={s.chipSt} style={{ background: 'var(--warn-bg)', color: 'var(--warn)' }}>{tx('qe.40')}</span>}
          </>
        )}
        actions={(
          <>
            <HeaderTool label="Thao tác khác" icon={IconDots} onClick={noWrite} />
            <Button variant="primary" icon={<IconSend size={16} stroke={1.8} />} onClick={noWrite}>{tx('qe.41')}</Button>
          </>
        )}
      />
      <div className={s.cols}>
        <div className={s.left}>
          {state === 'error' ? (
            <section aria-label="Lỗi">
              <StateView
                state="error"
                title="Không tải được bảng giá hãng"
                desc="Bản nháp vẫn được giữ. Thử lại hoặc soạn tay phương án cước."
                action={<Button onClick={() => void rates.refetch()}>{tx('qe.42')}</Button>}
              />
            </section>
          ) : state === 'loading' ? (
            <StateView state="loading" skeleton={<div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>{[0, 1, 2].map((i) => <section key={i} className={s.skel} aria-label="Đang tải"><Skeleton rows={3} /></section>)}</div>} />
          ) : (
            left
          )}
        </div>
        <aside className={s.right} aria-label="Kiểm tra, tổng và xem trước">{right}</aside>
      </div>
    </Page>
  );
}
